import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    reporter_name: '',
    reporter_email: '',
    report_type: 'inappropriate_content',
    content_url: '',
    description: '',
    additional_info: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/content-reports`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit report:', error);
      alert('Failed to send report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const reportTypes = [
    { value: 'inappropriate_content', label: 'Inappropriate Content' },
    { value: 'copyright_violation', label: 'Copyright Violation' },
    { value: 'spam', label: 'Spam or Malicious Content' },
    { value: 'technical_issue', label: 'Technical Issue' },
    { value: 'privacy_violation', label: 'Privacy Violation' },
    { value: 'other', label: 'Other' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900">
        <header className="sticky top-0 z-50 glass-effect-dark border-b border-orange-500/20">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-gray-300 hover:text-orange-400 hover:bg-orange-600/10"
                data-testid="back-home-btn"
              >
                ← Back to Home
              </Button>
              <div className="flex items-center space-x-3">
                <div className="text-2xl font-bold text-white">
                  <span className="text-gradient">X</span>videxa
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="min-h-screen flex items-center justify-center" data-testid="report-success">
          <Card className="glass-effect-dark max-w-md">
            <CardContent className="p-8 text-center">
              <div className="text-6xl mb-4">🛡️</div>
              <h2 className="text-2xl font-bold text-orange-400 mb-4">Report Submitted!</h2>
              <p className="text-gray-300 mb-6">
                Thank you for helping keep Megavideosee safe. Your report has been received and will be reviewed by our moderation team within 24 hours.
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="btn-primary"
              >
                Back to Homepage
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="report-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-effect-dark border-b border-orange-500/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-gray-300 hover:text-orange-400 hover:bg-orange-600/10"
              data-testid="back-home-btn"
            >
              ← Back to Home
            </Button>
            <div className="flex items-center space-x-3">
              <div className="text-2xl font-bold text-white">
                <span className="text-gradient">X</span>videxa
              </div>
              <div className="text-orange-400 text-sm bg-orange-600/20 px-2 py-1 rounded border border-orange-500/50">
                Report Content
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-3xl">
        <div className="animate-slideInUp">
          
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold title-primary mb-4">
              Report Content
            </h1>
            <p className="text-gray-400 text-lg">
              Help us maintain a safe and appropriate platform by reporting problematic content
            </p>
          </div>

          {/* Report Guidelines */}
          <Card className="glass-effect-dark mb-8">
            <CardHeader>
              <CardTitle className="text-orange-400 flex items-center">
                <span className="mr-2">🛡️</span>
                Reporting Guidelines
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white font-semibold mb-2">What to Report:</h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Inappropriate or illegal content</li>
                    <li>• Copyright violations</li>
                    <li>• Spam or malicious content</li>
                    <li>• Privacy violations</li>
                    <li>• Technical issues affecting user experience</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Response Time:</h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Reports reviewed within 24 hours</li>
                    <li>• Urgent issues prioritized</li>
                    <li>• Anonymous reporting available</li>
                    <li>• Follow-up via email if provided</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Form */}
          <Card className="glass-effect-dark">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-orange-400">
                Submit a Report
              </CardTitle>
              <CardDescription className="text-gray-400">
                Please provide as much detail as possible to help us investigate your report
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Reporter Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="reporter_name" className="text-white mb-2 block">Your Name (Optional)</Label>
                    <Input
                      id="reporter_name"
                      name="reporter_name"
                      type="text"
                      value={formData.reporter_name}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Leave blank for anonymous reporting"
                      data-testid="report-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reporter_email" className="text-white mb-2 block">Email (Optional)</Label>
                    <Input
                      id="reporter_email"
                      name="reporter_email"
                      type="email"
                      value={formData.reporter_email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="For follow-up communication"
                      data-testid="report-email-input"
                    />
                  </div>
                </div>

                {/* Report Type */}
                <div>
                  <Label htmlFor="report_type" className="text-white mb-2 block">Report Type *</Label>
                  <select
                    id="report_type"
                    name="report_type"
                    value={formData.report_type}
                    onChange={handleInputChange}
                    className="form-input w-full"
                    required
                    data-testid="report-type-select"
                  >
                    {reportTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Content URL */}
                <div>
                  <Label htmlFor="content_url" className="text-white mb-2 block">Content URL (Optional)</Label>
                  <Input
                    id="content_url"
                    name="content_url"
                    type="url"
                    value={formData.content_url}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Link to the specific content you're reporting"
                    data-testid="report-url-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description" className="text-white mb-2 block">Description of Issue *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    required
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-input min-h-[100px] resize-none"
                    placeholder="Please describe the issue in detail..."
                    data-testid="report-description-input"
                  />
                </div>

                {/* Additional Information */}
                <div>
                  <Label htmlFor="additional_info" className="text-white mb-2 block">Additional Information</Label>
                  <Textarea
                    id="additional_info"
                    name="additional_info"
                    value={formData.additional_info}
                    onChange={handleInputChange}
                    className="form-input min-h-[80px] resize-none"
                    placeholder="Any additional context or information that might help..."
                    data-testid="report-additional-input"
                  />
                </div>

                {/* Important Notice */}
                <div className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="text-orange-400 text-xl">⚠️</div>
                    <div className="text-sm text-gray-300">
                      <p className="font-medium text-orange-400 mb-1">Important Notice</p>
                      <p>
                        False reports may result in restrictions on your ability to use our reporting system. 
                        Please ensure your report is legitimate and accurate. All reports are logged and reviewed by our moderation team.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary hover-lift text-lg py-3"
                  data-testid="report-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-pulse">Submitting Report...</div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Submit Report</span>
                      <span>🛡️</span>
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReportPage;